/**
 * Notification API Service
 * Handles all API calls related to notifications and push subscriptions
 */

import apiInstance from './apiAuth';

const getAuthHeaders = () => ({
  Authorization: `Token ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json'
});

export const notificationApi = {
  /**
   * Register push device subscription with backend
   * @param {Object} subscriptionData - Push subscription object
   * @returns {Promise}
   */
  registerDevice: async (subscriptionData) => {
    try {
      const response = await apiInstance.post(
        'push-devices/',
        subscriptionData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error registering device:', error);
      throw error;
    }
  },

  /**
   * Update existing device subscription
   * @param {number} deviceId - Device ID
   * @param {Object} subscriptionData - Updated subscription data
   * @returns {Promise}
   */
  updateDevice: async (deviceId, subscriptionData) => {
    try {
      const response = await apiInstance.put(
        `push-devices/${deviceId}/`,
        subscriptionData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  },

  /**
   * Delete/unregister device subscription
   * @param {number} deviceId - Device ID
   * @returns {Promise}
   */
  deleteDevice: async (deviceId) => {
    try {
      const response = await apiInstance.delete(
        `push-devices/${deviceId}/`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  /**
   * Get all registered devices for current user
   * @returns {Promise}
   */
  getDevices: async () => {
    try {
      const response = await apiInstance.get(
        'push-devices/',
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  /**
   * Get all notifications for current user
   * @param {Object} params - Query parameters (page, limit, is_read, etc.)
   * @returns {Promise}
   */
  getNotifications: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `notifications/${queryString ? `?${queryString}` : ''}`;

      const response = await apiInstance.get(
        url,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count
   * @returns {Promise}
   */
  getUnreadCount: async () => {
    try {
      const response = await apiInstance.get(
        'notifications/unread-count/',
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Mark notification as read
   * @param {number} notificationId - Notification ID
   * @returns {Promise}
   */
  markAsRead: async (notificationId) => {
    try {
      const response = await apiInstance.patch(
        `notifications/${notificationId}/mark-read/`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark notification as unread
   * @param {number} notificationId - Notification ID
   * @returns {Promise}
   */
  markAsUnread: async (notificationId) => {
    try {
      const response = await apiInstance.patch(
        `notifications/${notificationId}/mark-unread/`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   * @returns {Promise}
   */
  markAllAsRead: async () => {
    try {
      const response = await apiInstance.post(
        'notifications/mark_all_read/',
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  },

  /**
   * Delete notification
   * @param {number} notificationId - Notification ID
   * @returns {Promise}
   */
  deleteNotification: async (notificationId) => {
    try {
      const response = await apiInstance.delete(
        `notifications/${notificationId}/`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  /**
   * Delete all notifications
   * @returns {Promise}
   */
  deleteAllNotifications: async () => {
    try {
      const response = await apiInstance.post(
        'notifications/delete_all/',
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      throw error;
    }
  },

  /**
   * Get notification settings for current user
   * @returns {Promise}
   */
  getSettings: async () => {
    try {
      const response = await apiInstance.get(
        'settings/',
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  },

  /**
   * Update notification settings
   * @param {Object} settings - Settings object
   * @returns {Promise}
   */
  updateSettings: async (settings) => {
    try {
      const response = await apiInstance.post(
        'settings/update_current/',
        settings,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  },

  /**
   * Send test notification
   * @returns {Promise}
   */
  sendTestNotification: async () => {
    try {
      const response = await apiInstance.post(
        'notifications/send-test/',
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }
};

export default notificationApi;
